import { forwardRef } from 'react';
import * as CalloutPrimitive from '@radix-ui/themes/components/callout';
import type {
  RadixCalloutIconProps,
  RadixCalloutRootProps,
  RadixCalloutTextProps,
} from '@/types/radix-ui';
import { useThemeContext } from '@radix-ui/themes';
import clsx from 'clsx';
import { Flex, Text } from '@/components/ui';
import { resolveColor } from '@/utils/colorUtils';

/**
 * Callout component - enhanced wrapper around Radix UI Callout
 * Provides both simple API with props and advanced API with subcomponents
 */

export type CalloutColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'destructive'
  | 'orange'
  | 'blue'
  | 'red'
  | 'green'
  | 'gray'
  | 'teal';

export type CalloutSize = 'xsmall' | 'small' | 'medium' | 'large';

interface BaseCalloutRootProps {
  color?: CalloutColor;
  size?: CalloutSize;
}

export type CalloutRootProps = Omit<RadixCalloutRootProps, 'color' | 'size'> &
  BaseCalloutRootProps;
export type CalloutIconProps = RadixCalloutIconProps;
export type CalloutTextProps = RadixCalloutTextProps;

// Simple API props
interface CalloutSimpleProps {
  icon?: React.ReactNode;
  title?: React.ReactNode;
  children?: React.ReactNode;
  actions?: React.ReactNode;
  color?: CalloutColor;
  size?: CalloutSize;
  className?: string;
}

export type CalloutProps = CalloutSimpleProps | CalloutRootProps;

const sizeMapping = {
  xsmall: '1',
  small: '1',
  medium: '2',
  large: '3',
} as const;

// Simple API Callout component
const Callout = forwardRef<HTMLDivElement, CalloutProps>((props, ref) => {
  const theme = useThemeContext();
  const accentColor = theme?.accentColor;

  // Check if it's simple API (has icon, title, children, or actions props)
  const isSimpleAPI =
    'icon' in props ||
    'title' in props ||
    ('children' in props && typeof props.children !== 'undefined') ||
    'actions' in props;

  if (isSimpleAPI) {
    const {
      icon,
      title,
      children,
      actions,
      color = 'warning',
      size = 'small',
      className,
      ...rest
    } = props as CalloutSimpleProps;

    const radixColor = resolveColor<RadixCalloutRootProps['color']>(
      color,
      accentColor || undefined
    );
    const radixSize = sizeMapping[size];

    return (
      <CalloutPrimitive.Root
        ref={ref}
        color={radixColor}
        size={radixSize}
        className={clsx(
          'callout',
          `callout-size-${size}`,
          `callout-color-${color}`,
          className
        )}
        {...rest}
      >
        {/*<Flex gap="3" align="start" width="100%">*/}
        {icon && <CalloutPrimitive.Icon>{icon}</CalloutPrimitive.Icon>}
        <Flex direction="column" gap="1">
          {title && (
            <Text size="2xsmall" weight="medium" as="div">
              {title}
            </Text>
          )}
          {children && (
            <CalloutPrimitive.Text size="1">{children}</CalloutPrimitive.Text>
          )}
        </Flex>
        {actions && (
          <Flex ml="auto" gap="2" align="center">
            {actions}
          </Flex>
        )}
        {/*</Flex>*/}
      </CalloutPrimitive.Root>
    );
  }

  // Advanced API - render CalloutRoot
  const rootProps = props as CalloutRootProps;
  const { color = 'warning', size = 'small', className, ...rest } = rootProps;

  const accentColorWithFallback = accentColor || 'indigo';
  const radixColor = resolveColor<RadixCalloutRootProps['color']>(
    color,
    accentColorWithFallback
  );
  const radixSize = sizeMapping[size];

  return (
    <CalloutPrimitive.Root
      ref={ref}
      color={radixColor}
      size={radixSize}
      className={clsx(
        'callout',
        `callout-size-${size}`,
        `callout-color-${color}`,
        className
      )}
      {...rest}
    />
  );
});

// Advanced API components
const CalloutRoot = forwardRef<HTMLDivElement, CalloutRootProps>(
  ({ color = 'warning', size = 'small', className, ...props }, ref) => {
    const theme = useThemeContext();
    const accentColor = theme?.accentColor;
    const radixColor = resolveColor<RadixCalloutRootProps['color']>(
      color,
      accentColor || undefined
    );
    const radixSize = sizeMapping[size];

    return (
      <CalloutPrimitive.Root
        ref={ref}
        color={radixColor}
        size={radixSize}
        className={clsx(
          'callout',
          `callout-size-${size}`,
          `callout-color-${color}`,
          className
        )}
        {...props}
      />
    );
  }
);

const CalloutIcon = forwardRef<HTMLDivElement, CalloutIconProps>(
  (props, ref) => {
    return <CalloutPrimitive.Icon ref={ref} {...props} />;
  }
);

const CalloutText = forwardRef<HTMLDivElement, CalloutTextProps>(
  (props, ref) => {
    return <CalloutPrimitive.Text ref={ref} {...props} />;
  }
);

Callout.displayName = 'Callout';
CalloutRoot.displayName = 'CalloutRoot';
CalloutIcon.displayName = 'CalloutIcon';
CalloutText.displayName = 'CalloutText';

export { Callout, CalloutRoot, CalloutIcon, CalloutText };
